import { Modal } from 'bootstrap';

const openDialogs = [];
let activeDialog = null;
let returnFocus = null;

/** Restore focus only to a control that still belongs to the current page. */
function restoreFocus(element) {
  if (element?.isConnected && !element.closest('[aria-hidden="true"]')) {
    element.focus();
  }
}

/** Finish one Bootstrap transition before showing the most recently requested dialog. */
function reconcileDialogs() {
  const nextDialog = openDialogs.at(-1) || null;

  if (activeDialog) {
    if (activeDialog !== nextDialog && activeDialog.phase === 'shown') {
      activeDialog.lastFocused = document.activeElement;
      activeDialog.coordinatedHide = true;
      activeDialog.modal.hide();
      activeDialog.coordinatedHide = false;
    }

    return;
  }

  if (nextDialog) {
    activeDialog = nextDialog;
    nextDialog.phase = 'showing';
    nextDialog.modal.show();
  } else {
    restoreFocus(returnFocus);
    returnFocus = null;
  }
}

/** Remove a closed or unmounted dialog without disturbing other open requests. */
function removeDialog(dialog) {
  const index = openDialogs.indexOf(dialog);

  if (index !== -1) {
    openDialogs.splice(index, 1);
  }
}

/**
 * Coordinate Bootstrap dialogs through public methods and lifecycle events.
 * A child temporarily hides its parent without changing the parent's model value.
 * Disposal waits for the hidden event so Bootstrap can release its backdrop and scroll lock.
 */
export function createModalController(element, onDismiss) {
  const dialog = {
    modal: new Modal(element),
    phase: 'hidden',
    requested: false,
    disposed: false,
    coordinatedHide: false,
    dismissed: false,
    lastFocused: null
  };

  /** Release Bootstrap and DOM listeners once no transition can still reference them. */
  function release() {
    element.removeEventListener('shown.bs.modal', onShown);
    element.removeEventListener('hide.bs.modal', onHide);
    element.removeEventListener('hidden.bs.modal', onHidden);
    dialog.modal.dispose();

    // Bootstrap may reattach the element if Vue unmounts during the opening animation.
    element.remove();
  }

  /** Reconcile requests that changed during the opening animation. */
  function onShown() {
    dialog.phase = 'shown';
    restoreFocus(dialog.lastFocused);
    reconcileDialogs();
  }

  /** Distinguish user dismissal from temporarily covering a parent dialog. */
  function onHide() {
    dialog.phase = 'hiding';

    if (!dialog.coordinatedHide) {
      dialog.requested = false;
      dialog.dismissed = true;
      removeDialog(dialog);
    }
  }

  /** Restore a waiting parent only after the previous backdrop and scroll lock are removed. */
  function onHidden() {
    dialog.phase = 'hidden';
    activeDialog = null;

    if (dialog.disposed) {
      release();
    } else if (dialog.dismissed) {
      dialog.dismissed = false;
      onDismiss();
    }

    reconcileDialogs();
  }

  element.addEventListener('shown.bs.modal', onShown);
  element.addEventListener('hide.bs.modal', onHide);
  element.addEventListener('hidden.bs.modal', onHidden);

  return {
    /** Add or remove this dialog's request, retaining any covered parent's form state. */
    setOpen(open) {
      if (dialog.disposed || dialog.requested === open) {
        return;
      }

      dialog.requested = open;

      if (open) {
        if (!activeDialog && openDialogs.length === 0) {
          returnFocus = document.activeElement;
        }

        dialog.lastFocused = null;
        openDialogs.push(dialog);
      } else {
        removeDialog(dialog);
      }

      reconcileDialogs();
    },

    /** Close an unmounted dialog before disposing its Bootstrap instance. */
    dispose() {
      dialog.disposed = true;
      dialog.requested = false;
      removeDialog(dialog);

      if (dialog.phase === 'hidden') {
        release();
      }

      reconcileDialogs();
    }
  };
}
